import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Easing,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  type LayoutChangeEvent,
  type ViewStyle,
} from "react-native";
import { AnimatePresence, MotiView } from "moti";
import {
  ArrowCircleLeftIcon,
  ArrowCircleRightIcon,
  CaretDownIcon,
  CaretRightIcon,
  MinusCircleIcon,
  PencilSimpleIcon,
  PlusCircleIcon,
  XIcon,
} from "@phosphor-icons/react";
import {
  SnapshotDiffChangeType,
  SnapshotSourceTag,
  type GetCourseVersionHistoryQuery,
  type GetSnapshotDiffQuery,
} from "@axio/graphql/generated/graphql";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import CustomHtml from "../../../../../components/common/general/CustomHtml/CustomHtml";
import { colors } from "../../../../../styles/design-system";
import { hasHtmlMarkup } from "../utils/hasHtmlMarkup";

const SHEET_WIDTH = 650;
const MODULE_DETAIL_FALLBACK_HEIGHT = 420;
const SNAPSHOT_RAIL_SCROLL_PADDING = 48;
const SHEET_ANIMATION_EASING = Easing.bezier(0.25, 0.1, 0.25, 1);
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export type VersionHistoryGroup = GetCourseVersionHistoryQuery["getCourseVersionHistory"]["groups"][number];
export type SnapshotVersionEntry = VersionHistoryGroup["snapshots"][number];
type SnapshotDiffResult = GetSnapshotDiffQuery["getSnapshotDiff"];
type SnapshotFieldChange = SnapshotDiffResult["courseFieldChanges"][number];
type SnapshotModuleDiff = SnapshotDiffResult["moduleDiffs"][number];
type SnapshotActivityDiff = SnapshotModuleDiff["activityDiffs"][number];
type VersionHistoryTab = "history" | "compare";
type VersionHistoryStatus = "loading" | "error" | "empty" | "ready";

interface VersionHistorySheetProps {
  open: boolean;
  courseName: string;
  versionGroups: VersionHistoryGroup[];
  currentVersion: SnapshotVersionEntry | null;
  selectedFromSnapshot: SnapshotVersionEntry | null;
  selectedToSnapshot: SnapshotVersionEntry | null;
  diff: SnapshotDiffResult | null;
  historyStatus: VersionHistoryStatus;
  historyErrorMessage?: string;
  isDiffLoading: boolean;
  diffErrorMessage?: string;
  onClose: () => void;
  onSelectSnapshotRow: (snapshotId: string) => void;
  onSelectFromSnapshot: (snapshotId: string) => void;
  onSelectToSnapshot: (snapshotId: string) => void;
}

function formatSnapshotDay(value: string): string {
  const dateOnlyMatch = DATE_ONLY_PATTERN.exec(value);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const yearNumber = Number(year);
    const monthNumber = Number(month);
    const dayNumber = Number(day);
    const date = new Date(Date.UTC(yearNumber, monthNumber - 1, dayNumber));

    if (
      date.getUTCFullYear() !== yearNumber ||
      date.getUTCMonth() !== monthNumber - 1 ||
      date.getUTCDate() !== dayNumber
    ) {
      return value.toUpperCase();
    }

    return formatUtcSnapshotDay(date);
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value.toUpperCase();
  }

  return formatUtcSnapshotDay(date);
}

function formatUtcSnapshotDay(date: Date): string {
  return date
    .toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    })
    .toUpperCase();
}

export function formatSnapshotTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const dateLabel = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const timeLabel = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  return `${dateLabel} at ${timeLabel}`;
}

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? `${count} ${singular}` : `${count} ${plural}`;
}

function getAuthorInitials(authorName: string): string {
  const initials = authorName
    .split(" ")
    .map((namePart) => namePart.trim()[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("");

  return initials.length > 0 ? initials.toUpperCase() : "UN";
}

function getSnapshotSourceTagLabel(sourceTag: SnapshotSourceTag): string {
  switch (sourceTag) {
    case SnapshotSourceTag.AiGeneration:
      return "AI generation";
    case SnapshotSourceTag.BatchApprove:
      return "Batch approval";
    case SnapshotSourceTag.ManualEdit:
      return "Manual edit";
    case SnapshotSourceTag.ManualReview:
      return "Manual review";
    case SnapshotSourceTag.ResetToIntake:
      return "Reset to intake";
    case SnapshotSourceTag.Restore:
      return "Restore";
    case SnapshotSourceTag.ViaAiAssistant:
      return "AI assistant";
  }

  return sourceTag;
}

const STORED_FIELD_LABELS: Record<string, string> = {
  recommendedTitle: "title",
  recommendedDescription: "description",
  recommendedValue: "content",
  recommendedPointsPossible: "points possible",
  recommendedDueDate: "due date",
};

const STORED_STATUS_LABELS: Record<string, string> = {
  UNDECIDED: "Undecided",
  QUEUED: "Queued",
  SKIPPED: "Skipped",
  PUBLISHED: "Published",
};

const DIFF_FIELD_LABELS: Record<string, string> = {
  activityOrder: "Activity order",
  moduleOrder: "Module order",
  status: "Review status",
};

function formatStoredSnapshotDescription(description: string): string {
  return description.replace(
    /recommendedTitle|recommendedDescription|recommendedValue|recommendedPointsPossible|recommendedDueDate/g,
    (fieldName) => STORED_FIELD_LABELS[fieldName] ?? fieldName
  );
}

function getSnapshotSummary(snapshot: SnapshotVersionEntry): string {
  return snapshot.description
    ? formatStoredSnapshotDescription(snapshot.description)
    : `${getSnapshotSourceTagLabel(snapshot.sourceTag)} snapshot`;
}

function formatFieldLabel(field: string): string {
  const diffFieldLabel = DIFF_FIELD_LABELS[field];
  if (diffFieldLabel) {
    return diffFieldLabel;
  }

  return field
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function isOrderDiffField(field: string): boolean {
  return field === "activityOrder" || field === "moduleOrder";
}

function formatStoredFieldValue(field: string, value: string | null): string | null {
  if (field !== "status" || value == null) {
    return value;
  }

  return STORED_STATUS_LABELS[value.toUpperCase()] ?? value;
}

function isRichTextField(field: string): boolean {
  const normalizedField = field.toLowerCase();
  return normalizedField.includes("description") || normalizedField.includes("content");
}

function getSnapshotDiffChangeCount(diff: SnapshotDiffResult): number {
  return (
    diff.courseFieldChanges.length +
    diff.moduleDiffs.reduce((sum, moduleDiff) => sum + getNestedModuleDiffChangeCount(moduleDiff), 0)
  );
}

function getActivityDiffChangeCount(activityDiff: SnapshotActivityDiff): number {
  return activityDiff.fields.length > 0 ? activityDiff.fields.length : 1;
}

function getNestedModuleDiffChangeCount(moduleDiff: SnapshotModuleDiff): number {
  const nestedChangeCount =
    moduleDiff.fields.length +
    moduleDiff.activityDiffs.reduce((sum, activityDiff) => sum + getActivityDiffChangeCount(activityDiff), 0);
  return nestedChangeCount > 0 ? nestedChangeCount : 1;
}

function getChangeTone(changeType: SnapshotDiffChangeType) {
  if (changeType === SnapshotDiffChangeType.Added) {
    return {
      icon: <PlusCircleIcon size={14} color={colors.success[700]} weight="regular" />,
      badgeStyle: styles.addedBadge,
      badgeTextStyle: styles.addedBadgeText,
      containerStyle: styles.addedDiffCard,
    };
  }

  if (changeType === SnapshotDiffChangeType.Removed) {
    return {
      icon: <MinusCircleIcon size={14} color={colors.error[600]} weight="regular" />,
      badgeStyle: styles.removedBadge,
      badgeTextStyle: styles.removedBadgeText,
      containerStyle: styles.removedDiffCard,
    };
  }

  return {
    icon: <PencilSimpleIcon size={14} color={colors.brand[600]} weight="regular" />,
    badgeStyle: styles.changedBadge,
    badgeTextStyle: styles.changedBadgeText,
    containerStyle: styles.changedDiffCard,
  };
}

function getChangeTypeLabel(changeType: SnapshotDiffChangeType): string {
  if (changeType === SnapshotDiffChangeType.Added) {
    return "Added";
  }

  if (changeType === SnapshotDiffChangeType.Removed) {
    return "Removed";
  }

  return "Changed";
}

function SnapshotPill({
  snapshot,
  selected,
  disabled,
  labelPrefix,
  onPress,
}: {
  snapshot: SnapshotVersionEntry;
  selected: boolean;
  disabled?: boolean;
  labelPrefix: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityState={{ selected, disabled }}
      accessibilityLabel={`${labelPrefix} v${snapshot.versionNumber} snapshot`}
      style={[styles.snapshotPill, selected && styles.snapshotPillSelected, disabled && styles.snapshotPillDisabled]}>
      <CustomText
        text={`v${snapshot.versionNumber}`}
        useTranslationText={false}
        size="xs"
        weight="bold"
        style={[styles.snapshotPillText, selected && styles.snapshotPillTextSelected]}
      />
      <CustomText
        text={formatSnapshotTime(snapshot.timestamp)}
        useTranslationText={false}
        size="xs"
        style={[styles.snapshotPillMeta, selected && styles.snapshotPillMetaSelected]}
      />
    </Pressable>
  );
}

function SnapshotSelectorRail({
  entries,
  selectedSnapshotId,
  disabledSnapshotId,
  labelPrefix,
  scrollLabel,
  onSelect,
}: {
  entries: SnapshotVersionEntry[];
  selectedSnapshotId: string;
  disabledSnapshotId: string;
  labelPrefix: string;
  scrollLabel: string;
  onSelect: (snapshotId: string) => void;
}) {
  const scrollRef = useRef<ScrollView | null>(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [scrollX, setScrollX] = useState(0);

  const maxScrollX = Math.max(contentWidth - viewportWidth, 0);
  const hasOverflow = maxScrollX > 1;
  const canScrollLeft = scrollX > 2;
  const canScrollRight = scrollX < maxScrollX - 2;

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    setScrollX(Math.max(event.nativeEvent.contentOffset.x, 0));
  };

  const handleScrollControl = (direction: -1 | 1) => {
    const scrollDistance = Math.max(viewportWidth - SNAPSHOT_RAIL_SCROLL_PADDING, 160);
    const nextScrollX = Math.min(Math.max(scrollX + direction * scrollDistance, 0), maxScrollX);
    scrollRef.current?.scrollTo({ x: nextScrollX, animated: true });
  };

  return (
    <View style={styles.selectorRailFrame} onLayout={(event) => setViewportWidth(event.nativeEvent.layout.width)}>
      {hasOverflow && canScrollLeft && (
        <Pressable
          onPress={() => handleScrollControl(-1)}
          accessibilityRole="button"
          accessibilityLabel={`Scroll ${scrollLabel} snapshots left`}
          style={[styles.selectorScrollButton, styles.selectorScrollButtonLeft]}>
          <ArrowCircleLeftIcon size={26} color={colors.grey[300]} weight="regular" />
        </Pressable>
      )}

      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={handleScroll}
        onContentSizeChange={(width) => setContentWidth(width)}
        style={styles.selectorScroll}
        contentContainerStyle={[styles.selectorRail, hasOverflow && styles.selectorRailOverflow]}>
        {entries.map((entry) => (
          <SnapshotPill
            key={`${scrollLabel}-${entry.id}`}
            snapshot={entry}
            selected={entry.id === selectedSnapshotId}
            disabled={entry.id === disabledSnapshotId}
            labelPrefix={labelPrefix}
            onPress={() => onSelect(entry.id)}
          />
        ))}
      </ScrollView>

      {hasOverflow && canScrollRight && (
        <Pressable
          onPress={() => handleScrollControl(1)}
          accessibilityRole="button"
          accessibilityLabel={`Scroll ${scrollLabel} snapshots right`}
          style={[styles.selectorScrollButton, styles.selectorScrollButtonRight]}>
          <ArrowCircleRightIcon size={26} color={colors.grey[300]} weight="regular" />
        </Pressable>
      )}
    </View>
  );
}

function VersionRow({
  entry,
  selected,
  onSelect,
}: {
  entry: SnapshotVersionEntry;
  selected: boolean;
  onSelect: (snapshotId: string) => void;
}) {
  const handlePress = () => {
    if (entry.isCurrent) {
      return;
    }

    onSelect(entry.id);
  };

  return (
    <View style={styles.versionRowWrap}>
      <Pressable
        onPress={handlePress}
        disabled={entry.isCurrent}
        accessibilityRole="button"
        accessibilityState={{ selected, disabled: entry.isCurrent }}
        accessibilityLabel={
          entry.isCurrent
            ? `v${entry.versionNumber} (current)`
            : `View v${entry.versionNumber} from ${formatSnapshotTime(entry.timestamp)}`
        }
        style={[styles.versionRow, selected && styles.versionRowSelected, entry.isCurrent && styles.versionRowCurrent]}>
        <View style={styles.avatar}>
          <CustomText
            text={getAuthorInitials(entry.authorName)}
            useTranslationText={false}
            size="xs"
            weight="bold"
            style={styles.avatarText}
          />
        </View>
        <View style={styles.versionRowBody}>
          <View style={styles.versionMetaRow}>
            <CustomText
              text={`v${entry.versionNumber}`}
              useTranslationText={false}
              size="s"
              weight="bold"
              style={styles.versionNumber}
            />
            {entry.isCurrent && (
              <View style={styles.currentBadge}>
                <CustomText
                  text="Current"
                  useTranslationText={false}
                  size="xs"
                  weight="medium"
                  style={styles.currentBadgeText}
                />
              </View>
            )}
            <CustomText text="·" useTranslationText={false} size="xs" style={styles.versionDot} />
            <CustomText
              text={formatSnapshotTime(entry.timestamp)}
              useTranslationText={false}
              size="xs"
              style={styles.versionMeta}
            />
          </View>
          <CustomText
            text={entry.authorName}
            useTranslationText={false}
            size="xs"
            weight="medium"
            style={styles.versionAuthor}
          />
          <CustomText
            text={getSnapshotSummary(entry)}
            useTranslationText={false}
            size="s"
            weight="medium"
            style={styles.versionSummary}
          />
          <View style={styles.versionRowFooter}>
            <View style={styles.methodBadge}>
              <CustomText
                text={getSnapshotSourceTagLabel(entry.sourceTag)}
                useTranslationText={false}
                size="xs"
                weight="medium"
                style={styles.methodBadgeText}
              />
            </View>
          </View>
        </View>
      </Pressable>
    </View>
  );
}

function ComparePanel({
  entries,
  fromSnapshot,
  toSnapshot,
  diff,
  isDiffLoading,
  diffErrorMessage,
  onSelectFrom,
  onSelectTo,
}: {
  entries: SnapshotVersionEntry[];
  fromSnapshot: SnapshotVersionEntry;
  toSnapshot: SnapshotVersionEntry;
  diff: SnapshotDiffResult | null;
  isDiffLoading: boolean;
  diffErrorMessage?: string;
  onSelectFrom: (snapshotId: string) => void;
  onSelectTo: (snapshotId: string) => void;
}) {
  const changeCount = diff ? getSnapshotDiffChangeCount(diff) : 0;
  const versionStepCount = Math.abs(toSnapshot.versionNumber - fromSnapshot.versionNumber);
  const comparisonMeta = isDiffLoading
    ? "Loading comparison..."
    : diffErrorMessage
      ? "Comparison unavailable"
      : `${pluralize(changeCount, "entity-level change", "entity-level changes")} across ${pluralize(
          versionStepCount,
          "version step",
          "version steps"
        )}`;

  return (
    <MotiView
      from={{ opacity: 0, translateY: 8 }}
      animate={{ opacity: 1, translateY: 0 }}
      transition={{ type: "timing", duration: 180, easing: SHEET_ANIMATION_EASING }}
      style={styles.comparePanel}>
      <View style={styles.compareHeader}>
        <View>
          <CustomText
            text={`Comparing v${fromSnapshot.versionNumber} to v${toSnapshot.versionNumber}`}
            useTranslationText={false}
            size="s"
            weight="bold"
            style={styles.compareTitle}
          />
          <CustomText text={comparisonMeta} useTranslationText={false} size="xs" style={styles.compareMeta} />
        </View>
      </View>

      <View style={styles.selectorBlock}>
        <CustomText
          text="Base snapshot"
          useTranslationText={false}
          size="xs"
          weight="bold"
          style={styles.selectorLabel}
        />
        <SnapshotSelectorRail
          entries={entries}
          selectedSnapshotId={fromSnapshot.id}
          disabledSnapshotId={toSnapshot.id}
          labelPrefix="Use"
          scrollLabel="base"
          onSelect={onSelectFrom}
        />
      </View>

      <View style={styles.selectorBlock}>
        <CustomText
          text="Target snapshot"
          useTranslationText={false}
          size="xs"
          weight="bold"
          style={styles.selectorLabel}
        />
        <SnapshotSelectorRail
          entries={entries}
          selectedSnapshotId={toSnapshot.id}
          disabledSnapshotId={fromSnapshot.id}
          labelPrefix="Compare to"
          scrollLabel="target"
          onSelect={onSelectTo}
        />
      </View>
    </MotiView>
  );
}

function DiffViewer({
  diff,
  isLoading,
  errorMessage,
  expandedModuleIds,
  onToggleModule,
}: {
  diff: SnapshotDiffResult | null;
  isLoading: boolean;
  errorMessage?: string;
  expandedModuleIds: Set<string>;
  onToggleModule: (moduleId: string) => void;
}) {
  if (isLoading) {
    return <SheetStateMessage text="Loading snapshot diff..." />;
  }

  if (errorMessage) {
    return <SheetStateMessage text={errorMessage} />;
  }

  if (!diff) {
    return <SheetStateMessage text="Select two snapshots to compare." />;
  }

  if (diff.courseFieldChanges.length === 0 && diff.moduleDiffs.length === 0) {
    return (
      <MotiView
        key={`${diff.snapshotIdA}-${diff.snapshotIdB}-empty`}
        from={{ opacity: 0, translateY: 8 }}
        animate={{ opacity: 1, translateY: 0 }}
        exit={{ opacity: 0, translateY: -4 }}
        transition={{ type: "timing", duration: 180, easing: SHEET_ANIMATION_EASING }}
        style={styles.emptyDiff}>
        <CustomText
          text="No changes between these snapshots."
          useTranslationText={false}
          size="s"
          style={styles.emptyDiffText}
        />
      </MotiView>
    );
  }

  return (
    <MotiView
      key={`${diff.snapshotIdA}-${diff.snapshotIdB}`}
      from={{ opacity: 0, translateY: 8 }}
      animate={{ opacity: 1, translateY: 0 }}
      exit={{ opacity: 0, translateY: -4 }}
      transition={{ type: "timing", duration: 180, easing: SHEET_ANIMATION_EASING }}
      style={styles.diffViewer}>
      {diff.courseFieldChanges.length > 0 && (
        <View style={styles.diffSection}>
          <CustomText
            text="Course fields"
            useTranslationText={false}
            size="xs"
            weight="bold"
            style={styles.diffSectionTitle}
          />
          {diff.courseFieldChanges.map((change) => (
            <FieldChangeRow key={`${change.field}-${change.before}-${change.after}`} change={change} />
          ))}
        </View>
      )}

      {diff.moduleDiffs.map((moduleDiff, index) => (
        <ModuleDiffCard
          key={`${diff.snapshotIdA}-${diff.snapshotIdB}-${moduleDiff.moduleId}-${index}`}
          moduleDiff={moduleDiff}
          expanded={expandedModuleIds.has(moduleDiff.moduleId)}
          onToggle={() => onToggleModule(moduleDiff.moduleId)}
        />
      ))}
    </MotiView>
  );
}

function ModuleDiffCard({
  moduleDiff,
  expanded,
  onToggle,
}: {
  moduleDiff: SnapshotModuleDiff;
  expanded: boolean;
  onToggle: () => void;
}) {
  const tone = getChangeTone(moduleDiff.changeType);
  const nestedChangeCount = getNestedModuleDiffChangeCount(moduleDiff);
  const [moduleBodyHeight, setModuleBodyHeight] = useState(0);
  const animatedBodyHeight = moduleBodyHeight || MODULE_DETAIL_FALLBACK_HEIGHT;
  const moduleTitle = moduleDiff.moduleTitle ?? "Untitled module";

  const handleModuleBodyLayout = (event: LayoutChangeEvent) => {
    const nextHeight = Math.ceil(event.nativeEvent.layout.height);
    if (nextHeight > 0 && nextHeight !== moduleBodyHeight) {
      setModuleBodyHeight(nextHeight);
    }
  };

  return (
    <View style={[styles.moduleDiffCard, tone.containerStyle]}>
      <Pressable
        onPress={onToggle}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={`${expanded ? "Collapse" : "Expand"} ${moduleTitle} diff`}
        style={styles.moduleDiffHeader}>
        <View style={styles.moduleDiffTitleRow}>
          {tone.icon}
          <View style={styles.moduleDiffTitleText}>
            <CustomText
              text={moduleTitle}
              useTranslationText={false}
              size="s"
              weight="bold"
              style={styles.moduleDiffTitle}
            />
            <CustomText
              text={`${pluralize(nestedChangeCount, "change", "changes")} in this module`}
              useTranslationText={false}
              size="xs"
              style={styles.moduleDiffMeta}
            />
          </View>
        </View>
        <View style={styles.moduleDiffHeaderRight}>
          <View style={[styles.changeBadge, tone.badgeStyle]}>
            <CustomText
              text={getChangeTypeLabel(moduleDiff.changeType)}
              useTranslationText={false}
              size="xs"
              weight="bold"
              style={[styles.changeBadgeText, tone.badgeTextStyle]}
            />
          </View>
          {expanded ? (
            <CaretDownIcon size={16} color={colors.grey[500]} weight="bold" />
          ) : (
            <CaretRightIcon size={16} color={colors.grey[500]} weight="bold" />
          )}
        </View>
      </Pressable>

      <AnimatePresence initial={false}>
        {expanded && (
          <MotiView
            key={`${moduleDiff.moduleId}-body`}
            from={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: animatedBodyHeight }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ type: "timing", duration: 200, easing: SHEET_ANIMATION_EASING }}
            style={styles.moduleDiffBodyAnimation}>
            <View style={styles.moduleDiffBody} onLayout={handleModuleBodyLayout}>
              {moduleDiff.fields.map((change) => (
                <FieldChangeRow
                  key={`${moduleDiff.moduleId}-${change.field}`}
                  change={change}
                  entityChangeType={moduleDiff.changeType}
                />
              ))}
              {moduleDiff.activityDiffs.map((activityDiff) => (
                <ActivityDiffCard key={activityDiff.activityId} activityDiff={activityDiff} />
              ))}
            </View>
          </MotiView>
        )}
      </AnimatePresence>
    </View>
  );
}

function ActivityDiffCard({ activityDiff }: { activityDiff: SnapshotActivityDiff }) {
  const tone = getChangeTone(activityDiff.changeType);
  const activityTitle = activityDiff.activityTitle ?? "Untitled activity";

  return (
    <View style={[styles.activityDiffCard, tone.containerStyle]}>
      <View style={styles.activityHeader}>
        <View style={styles.activityTitleWrap}>
          {tone.icon}
          <View style={styles.activityTitleText}>
            <CustomText
              text={activityTitle}
              useTranslationText={false}
              size="s"
              weight="bold"
              style={styles.activityTitle}
            />
            <CustomText
              text={activityDiff.activityType}
              useTranslationText={false}
              size="xs"
              style={styles.activityMeta}
            />
          </View>
        </View>
        <View style={[styles.changeBadge, tone.badgeStyle]}>
          <CustomText
            text={getChangeTypeLabel(activityDiff.changeType)}
            useTranslationText={false}
            size="xs"
            weight="bold"
            style={[styles.changeBadgeText, tone.badgeTextStyle]}
          />
        </View>
      </View>
      {activityDiff.fields.map((change) => (
        <FieldChangeRow
          key={`${activityDiff.activityId}-${change.field}`}
          change={change}
          entityChangeType={activityDiff.changeType}
        />
      ))}
    </View>
  );
}

function FieldChangeRow({
  change,
  entityChangeType,
}: {
  change: SnapshotFieldChange;
  entityChangeType?: SnapshotDiffChangeType;
}) {
  if (isOrderDiffField(change.field)) {
    return (
      <View style={styles.fieldChange}>
        <View style={styles.orderChangeSummary}>
          <CustomText
            text={`${formatFieldLabel(change.field)} changed`}
            useTranslationText={false}
            size="xs"
            weight="bold"
            style={styles.orderChangeText}
          />
        </View>
      </View>
    );
  }

  if (entityChangeType === SnapshotDiffChangeType.Added && change.before == null) {
    return (
      <View style={styles.fieldChange}>
        <CustomText
          text={formatFieldLabel(change.field)}
          useTranslationText={false}
          size="xs"
          weight="bold"
          style={styles.fieldLabel}
        />
        <ValueBlock field={change.field} label="Added" value={change.after} tone="added" />
      </View>
    );
  }

  if (entityChangeType === SnapshotDiffChangeType.Removed && change.after == null) {
    return (
      <View style={styles.fieldChange}>
        <CustomText
          text={formatFieldLabel(change.field)}
          useTranslationText={false}
          size="xs"
          weight="bold"
          style={styles.fieldLabel}
        />
        <ValueBlock field={change.field} label="Removed" value={change.before} tone="removed" />
      </View>
    );
  }

  return (
    <View style={styles.fieldChange}>
      <CustomText
        text={formatFieldLabel(change.field)}
        useTranslationText={false}
        size="xs"
        weight="bold"
        style={styles.fieldLabel}
      />
      <View style={styles.beforeAfterGrid}>
        <ValueBlock field={change.field} label="Before" value={change.before} tone="removed" />
        <ValueBlock field={change.field} label="After" value={change.after} tone="added" />
      </View>
    </View>
  );
}

function ValueBlock({
  field,
  label,
  value,
  tone,
}: {
  field: string;
  label: string;
  value: string | null;
  tone: "added" | "removed";
}) {
  const displayValue = formatStoredFieldValue(field, value);
  const shouldRenderRichText = Boolean(displayValue && isRichTextField(field) && hasHtmlMarkup(displayValue));

  return (
    <View style={[styles.valueBlock, tone === "added" ? styles.valueBlockAdded : styles.valueBlockRemoved]}>
      <CustomText
        text={label}
        useTranslationText={false}
        size="xs"
        weight="bold"
        style={[styles.valueLabel, tone === "added" ? styles.valueLabelAdded : styles.valueLabelRemoved]}
      />
      {shouldRenderRichText ? (
        <CustomHtml html={displayValue ?? ""} style={snapshotRichContentStyle} />
      ) : (
        <CustomText text={displayValue ?? "Not set"} useTranslationText={false} size="xs" style={styles.valueText} />
      )}
    </View>
  );
}

function SheetStateMessage({ text }: { text: string }) {
  return (
    <MotiView
      from={{ opacity: 0, translateY: 8 }}
      animate={{ opacity: 1, translateY: 0 }}
      exit={{ opacity: 0, translateY: -4 }}
      transition={{ type: "timing", duration: 180, easing: SHEET_ANIMATION_EASING }}
      style={styles.stateCard}>
      <CustomText text={text} useTranslationText={false} size="s" style={styles.stateText} />
    </MotiView>
  );
}

function getHeaderDescription(status: VersionHistoryStatus, currentVersion: SnapshotVersionEntry | null): string {
  if (currentVersion) {
    return `Current version: v${currentVersion.versionNumber} · Last changed ${formatSnapshotTime(
      currentVersion.timestamp
    )}`;
  }

  if (status === "loading") {
    return "Loading version history...";
  }

  if (status === "error") {
    return "Version history unavailable.";
  }

  return "No snapshots have been captured yet.";
}

function VersionHistorySheet({
  open,
  courseName,
  versionGroups,
  currentVersion,
  selectedFromSnapshot,
  selectedToSnapshot,
  diff,
  historyStatus,
  historyErrorMessage,
  isDiffLoading,
  diffErrorMessage,
  onClose,
  onSelectSnapshotRow,
  onSelectFromSnapshot,
  onSelectToSnapshot,
}: VersionHistorySheetProps) {
  const entries = useMemo(() => versionGroups.flatMap((group) => group.snapshots), [versionGroups]);
  const [activeTab, setActiveTab] = useState<VersionHistoryTab>("history");
  const [expandedModuleIds, setExpandedModuleIds] = useState<Set<string>>(() => new Set());
  const canCompare = entries.length >= 2;

  useEffect(() => {
    if (open) {
      setActiveTab("history");
    }
  }, [open]);

  useEffect(() => {
    if (!canCompare && activeTab === "compare") {
      setActiveTab("history");
    }
  }, [activeTab, canCompare]);

  useEffect(() => {
    setExpandedModuleIds(new Set(diff?.moduleDiffs.slice(0, 1).map((moduleDiff) => moduleDiff.moduleId) ?? []));
  }, [diff]);

  const handleToggleModule = useCallback((moduleId: string) => {
    setExpandedModuleIds((current) => {
      const next = new Set(current);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  }, []);

  const tablistLabel = "Version history tabs";
  const headerDescription = getHeaderDescription(historyStatus, currentVersion);

  return (
    <AnimatePresence>
      {open && (
        <MotiView
          key="version-history-overlay"
          from={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: "timing", duration: 150 }}
          style={styles.overlay}>
          <Pressable style={styles.overlayBackdrop} onPress={onClose} />
          <MotiView
            key="version-history-panel"
            from={{ translateX: SHEET_WIDTH }}
            animate={{ translateX: 0 }}
            exit={{ translateX: SHEET_WIDTH }}
            transition={{ type: "timing", duration: 220 }}
            style={styles.sheet}
            accessibilityViewIsModal
            accessibilityLabel="Version history">
            <View style={styles.header}>
              <View style={styles.headerTop}>
                <View style={styles.headerCopy}>
                  <CustomText
                    text="Version history"
                    useTranslationText={false}
                    size="l"
                    weight="bold"
                    role="heading"
                    aria-level="2"
                    style={styles.title}
                  />
                  <CustomText text={courseName} useTranslationText={false} size="s" style={styles.courseName} />
                  <CustomText
                    text={headerDescription}
                    useTranslationText={false}
                    size="xs"
                    style={styles.description}
                  />
                </View>
                <Pressable onPress={onClose} style={styles.closeButton} accessibilityLabel="Close version history">
                  <XIcon size={18} color={colors.grey[500]} weight="regular" />
                </Pressable>
              </View>
            </View>

            <View
              style={styles.tabRow}
              accessibilityRole="tablist"
              accessibilityLabel={tablistLabel}
              role="tablist"
              aria-label={tablistLabel}>
              <Pressable
                onPress={() => setActiveTab("history")}
                style={[styles.tab, activeTab === "history" && styles.tabActive]}
                accessibilityRole="tab"
                accessibilityState={{ selected: activeTab === "history" }}
                accessibilityLabel="History"
                role="tab"
                aria-selected={activeTab === "history"}
                aria-label="History">
                <CustomText
                  text="History"
                  useTranslationText={false}
                  size="s"
                  weight="medium"
                  style={[styles.tabText, activeTab === "history" && styles.tabActiveText]}
                />
              </Pressable>
              <Pressable
                onPress={() => setActiveTab("compare")}
                disabled={!canCompare}
                style={[styles.tab, activeTab === "compare" && styles.tabActive, !canCompare && styles.tabDisabled]}
                accessibilityRole="tab"
                accessibilityState={{ selected: activeTab === "compare", disabled: !canCompare }}
                accessibilityLabel="Compare"
                role="tab"
                aria-selected={activeTab === "compare"}
                aria-label="Compare">
                <CustomText
                  text="Compare"
                  useTranslationText={false}
                  size="s"
                  weight="medium"
                  style={[
                    styles.tabText,
                    activeTab === "compare" && styles.tabActiveText,
                    !canCompare && styles.tabDisabledText,
                  ]}
                />
              </Pressable>
            </View>

            <ScrollView style={styles.scrollArea} contentContainerStyle={styles.scrollContent}>
              {activeTab === "compare" ? (
                !canCompare ? (
                  <SheetStateMessage text="At least two snapshots are needed to compare versions." />
                ) : selectedFromSnapshot && selectedToSnapshot ? (
                  <>
                    <ComparePanel
                      entries={entries}
                      fromSnapshot={selectedFromSnapshot}
                      toSnapshot={selectedToSnapshot}
                      diff={diff}
                      isDiffLoading={isDiffLoading}
                      diffErrorMessage={diffErrorMessage}
                      onSelectFrom={onSelectFromSnapshot}
                      onSelectTo={onSelectToSnapshot}
                    />

                    <DiffViewer
                      diff={diff}
                      isLoading={isDiffLoading}
                      errorMessage={diffErrorMessage}
                      expandedModuleIds={expandedModuleIds}
                      onToggleModule={handleToggleModule}
                    />
                  </>
                ) : (
                  <SheetStateMessage text="Select two snapshots to compare." />
                )
              ) : (
                <View style={styles.historySection}>
                  {historyStatus === "loading" && <SheetStateMessage text="Loading version history..." />}
                  {historyStatus === "error" && (
                    <SheetStateMessage text={historyErrorMessage ?? "Unable to load version history."} />
                  )}
                  {historyStatus === "empty" && <SheetStateMessage text="No snapshots have been captured yet." />}
                  {historyStatus === "ready" &&
                    versionGroups.map((group, groupIndex) => (
                      <View key={group.date} style={groupIndex > 0 ? styles.dayGroupWithGap : styles.dayGroup}>
                        <View style={styles.dayHeader}>
                          <CustomText
                            text={formatSnapshotDay(group.date)}
                            useTranslationText={false}
                            size="xs"
                            weight="bold"
                            style={styles.dayLabel}
                          />
                          <View style={styles.dayRule} />
                        </View>
                        <View style={styles.versionRows}>
                          {group.snapshots.map((entry) => (
                            <VersionRow key={entry.id} entry={entry} selected={false} onSelect={onSelectSnapshotRow} />
                          ))}
                        </View>
                      </View>
                    ))}
                </View>
              )}
            </ScrollView>
          </MotiView>
        </MotiView>
      )}
    </AnimatePresence>
  );
}

const snapshotRichContentStyle = {
  fontSize: 14,
  lineHeight: "22px",
  color: "#344054",
};

const styles = StyleSheet.create({
  overlay: {
    ...(Platform.OS === "web"
      ? ({
          position: "fixed" as unknown as "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: 1000,
        } as ViewStyle)
      : {
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        }),
  },
  overlayBackdrop: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "rgba(255, 255, 255, 0.02)",
  },
  sheet: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: SHEET_WIDTH,
    maxWidth: "100%",
    backgroundColor: colors.white,
    borderLeftWidth: 1,
    borderLeftColor: colors.grey[200],
    flexDirection: "column",
    ...(Platform.OS === "web"
      ? ({
          boxShadow: "-8px 0 24px rgba(16, 24, 40, 0.06)",
        } as unknown as ViewStyle)
      : {
          shadowColor: "#101828",
          shadowOpacity: 0.06,
          shadowRadius: 24,
          shadowOffset: { width: -8, height: 0 },
        }),
  },
  header: {
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  headerTop: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  headerCopy: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  title: {
    color: colors.grey[900],
  },
  courseName: {
    color: colors.grey[700],
    lineHeight: 20,
  },
  description: {
    color: colors.grey[500],
    lineHeight: 16,
  },
  closeButton: {
    padding: 4,
    borderRadius: 6,
  },
  tabRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 32,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  tab: {
    paddingVertical: 12,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  tabActive: {
    borderBottomColor: colors.brand[600],
  },
  tabDisabled: {
    opacity: 0.45,
  },
  tabText: {
    color: colors.grey[700],
  },
  tabActiveText: {
    color: colors.brand[600],
  },
  tabDisabledText: {
    color: colors.grey[400],
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
  },
  scrollContent: {
    padding: 12,
    paddingBottom: 24,
  },
  comparePanel: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.grey[25],
    borderRadius: 12,
    padding: 12,
    gap: 12,
  },
  compareHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  compareTitle: {
    color: colors.grey[900],
    lineHeight: 20,
  },
  compareMeta: {
    color: colors.grey[500],
    lineHeight: 16,
  },
  stateCard: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 12,
    padding: 16,
    backgroundColor: colors.white,
  },
  stateText: {
    color: colors.grey[500],
    textAlign: "center",
  },
  selectorBlock: {
    gap: 6,
  },
  selectorLabel: {
    color: colors.grey[600],
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  selectorRailFrame: {
    position: "relative",
    minWidth: 0,
  },
  selectorScroll: {
    minWidth: 0,
  },
  selectorRail: {
    gap: 8,
    paddingRight: 4,
  },
  selectorRailOverflow: {
    paddingLeft: 2,
    paddingRight: 34,
  },
  selectorScrollButton: {
    position: "absolute",
    top: "50%",
    zIndex: 2,
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[100],
    transform: [{ translateY: -15 }],
    ...(Platform.OS === "web"
      ? ({
          boxShadow: "0 4px 12px rgba(16, 24, 40, 0.08)",
        } as unknown as ViewStyle)
      : {
          shadowColor: "#101828",
          shadowOpacity: 0.08,
          shadowRadius: 12,
          shadowOffset: { width: 0, height: 4 },
        }),
  },
  selectorScrollButtonLeft: {
    left: 0,
  },
  selectorScrollButtonRight: {
    right: 0,
  },
  snapshotPill: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 7,
    minWidth: 96,
    gap: 2,
  },
  snapshotPillSelected: {
    borderColor: colors.brand[400],
    backgroundColor: colors.brand[25],
  },
  snapshotPillDisabled: {
    opacity: 0.45,
  },
  snapshotPillText: {
    color: colors.grey[900],
    lineHeight: 14,
  },
  snapshotPillTextSelected: {
    color: colors.brand[700],
  },
  snapshotPillMeta: {
    color: colors.grey[500],
    fontSize: 10,
    lineHeight: 12,
  },
  snapshotPillMetaSelected: {
    color: colors.brand[600],
  },
  diffViewer: {
    marginTop: 12,
    gap: 10,
  },
  emptyDiff: {
    marginTop: 12,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 12,
    padding: 16,
    backgroundColor: colors.white,
  },
  emptyDiffText: {
    color: colors.grey[500],
    textAlign: "center",
  },
  diffSection: {
    gap: 8,
  },
  diffSectionTitle: {
    color: colors.grey[600],
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  moduleDiffCard: {
    borderWidth: 1,
    borderRadius: 12,
    overflow: "hidden",
  },
  addedDiffCard: {
    borderColor: colors.success[100],
    backgroundColor: colors.success[25],
  },
  removedDiffCard: {
    borderColor: colors.error[100],
    backgroundColor: colors.error[25],
  },
  changedDiffCard: {
    borderColor: colors.brand[100],
    backgroundColor: colors.white,
  },
  moduleDiffHeader: {
    padding: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  moduleDiffTitleRow: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  moduleDiffTitleText: {
    flex: 1,
    minWidth: 0,
  },
  moduleDiffTitle: {
    color: colors.grey[900],
    lineHeight: 18,
  },
  moduleDiffMeta: {
    color: colors.grey[500],
    lineHeight: 16,
  },
  moduleDiffHeaderRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  moduleDiffBody: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    padding: 12,
    gap: 10,
    backgroundColor: colors.white,
  },
  moduleDiffBodyAnimation: {
    overflow: "hidden",
  },
  activityDiffCard: {
    borderWidth: 1,
    borderRadius: 10,
    padding: 10,
    gap: 8,
  },
  activityHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
  },
  activityTitleWrap: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  activityTitleText: {
    flex: 1,
    minWidth: 0,
  },
  activityTitle: {
    color: colors.grey[900],
    lineHeight: 18,
  },
  activityMeta: {
    color: colors.grey[500],
    lineHeight: 16,
  },
  changeBadge: {
    borderRadius: 999,
    paddingHorizontal: 7,
    paddingVertical: 3,
  },
  changeBadgeText: {
    fontSize: 10,
    lineHeight: 12,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  addedBadge: {
    backgroundColor: colors.success[50],
  },
  addedBadgeText: {
    color: colors.success[700],
  },
  removedBadge: {
    backgroundColor: colors.error[50],
  },
  removedBadgeText: {
    color: colors.error[700],
  },
  changedBadge: {
    backgroundColor: colors.brand[50],
  },
  changedBadgeText: {
    color: colors.brand[700],
  },
  fieldChange: {
    gap: 6,
  },
  fieldLabel: {
    color: colors.grey[700],
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  orderChangeSummary: {
    alignSelf: "flex-start",
    borderWidth: 1,
    borderColor: colors.brand[100],
    borderRadius: 8,
    backgroundColor: colors.brand[25],
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  orderChangeText: {
    color: colors.brand[700],
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  beforeAfterGrid: {
    flexDirection: "row",
    gap: 8,
  },
  valueBlock: {
    flex: 1,
    minWidth: 0,
    borderWidth: 1,
    borderRadius: 8,
    padding: 8,
    gap: 4,
  },
  valueBlockAdded: {
    borderColor: colors.success[100],
    backgroundColor: colors.success[25],
  },
  valueBlockRemoved: {
    borderColor: colors.error[100],
    backgroundColor: colors.error[25],
  },
  valueLabel: {
    fontSize: 10,
    lineHeight: 12,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  valueLabelAdded: {
    color: colors.success[700],
  },
  valueLabelRemoved: {
    color: colors.error[700],
  },
  valueText: {
    color: colors.grey[800],
    lineHeight: 17,
  },
  historySection: {},
  dayGroup: {
    gap: 8,
  },
  dayGroupWithGap: {
    gap: 8,
    marginTop: 20,
  },
  dayHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 12,
  },
  dayLabel: {
    color: colors.grey[500],
    fontSize: 11,
    letterSpacing: 0.4,
  },
  dayRule: {
    flex: 1,
    height: 1,
    backgroundColor: colors.grey[200],
  },
  versionRows: {
    gap: 2,
  },
  versionRowWrap: {
    borderWidth: 1,
    borderColor: "transparent",
    borderRadius: 16,
    overflow: "visible",
  },
  versionRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    padding: 12,
    borderRadius: 16,
  },
  versionRowSelected: {
    backgroundColor: "rgba(230, 235, 255, 0.6)",
  },
  versionRowCurrent: {
    opacity: 0.72,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.grey[900],
  },
  avatarText: {
    color: colors.white,
    fontSize: 11,
  },
  versionRowBody: {
    flex: 1,
    minWidth: 0,
    gap: 3,
  },
  versionMetaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    flexWrap: "wrap",
  },
  versionNumber: {
    color: colors.grey[900],
    lineHeight: 18,
  },
  currentBadge: {
    height: 20,
    borderRadius: 999,
    backgroundColor: colors.brand[50],
    paddingHorizontal: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  currentBadgeText: {
    color: colors.brand[700],
    fontSize: 10,
    lineHeight: 12,
  },
  versionDot: {
    color: colors.grey[400],
  },
  versionMeta: {
    color: colors.grey[500],
    lineHeight: 16,
  },
  versionAuthor: {
    color: colors.grey[700],
    lineHeight: 16,
  },
  versionSummary: {
    color: colors.grey[900],
    lineHeight: 19,
  },
  versionRowFooter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 4,
  },
  methodBadge: {
    alignSelf: "flex-start",
    borderRadius: 4,
    backgroundColor: colors.grey[100],
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  methodBadgeText: {
    color: colors.grey[600],
    fontSize: 10,
    lineHeight: 12,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
});

export default VersionHistorySheet;
